import { memo, useMemo, useState, useRef, useEffect } from "react";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Edit2,
  Camera,
  CheckCircle,
  Trash2,
} from "lucide-react";
import { Button } from "@/app/components/button";
import { BottomNav } from "./bottom-nav";
import { formatCPF, formatPhoneNumber, formatDate, formatDateOnly, formatCEP } from "@/config/locale";
import type { OnboardingData } from "@/app/components/new-user-onboarding";
import * as authClient from "@/app/api/auth-client";
import type { Profile } from "@/app/api/auth-client";

interface ProfileData {
  fullName: string;
  preferredName?: string;
  cpf: string;
  email: string;
  phone: string;
  birthDate: string;
  gender: "male" | "female" | "other" | "prefer-not-to-say";
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    cep: string;
  };
  profileImage?: string;
  memberSince: string;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isCpfVerified: boolean;
}

interface ProfilePageProps {
  onBack: () => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: "home" | "favorites") => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
  onEditProfile: () => void;
  userProfile?: OnboardingData | null;
  userEmail?: string;
  /** From GET /auth/me when profile row is not loaded yet (e.g. skipped onboarding). */
  authUserName?: string;
  /** Called after profile photo is removed so the app can refresh. */
  onProfileUpdated?: (profile: Profile) => void;
  /** Bump (e.g. `myAdsRefreshKey`) to refetch “Anúncios Ativos” after creating an ad. */
  statsRefreshKey?: number;
}

function emptyProfileDisplay(emailFallback: string): ProfileData {
  return {
    fullName: "",
    preferredName: undefined,
    cpf: "",
    email: emailFallback.trim(),
    phone: "",
    birthDate: "",
    gender: "prefer-not-to-say",
    address: {
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      city: "",
      state: "SP",
      cep: "",
    },
    profileImage: undefined,
    memberSince: "",
    isEmailVerified: false,
    isPhoneVerified: false,
    isCpfVerified: false,
  };
}

export const ProfilePage = memo(function ProfilePage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
  onEditProfile,
  userProfile,
  userEmail,
  authUserName,
  onProfileUpdated,
  statsRefreshKey = 0,
}: ProfilePageProps) {
  const [photoMenuOpen, setPhotoMenuOpen] = useState(false);
  const [photoRemoving, setPhotoRemoving] = useState(false);
  const photoMenuRef = useRef<HTMLDivElement>(null);
  const [accountStatsLoading, setAccountStatsLoading] = useState(true);
  const [activeAdsCount, setActiveAdsCount] = useState(0);
  const [salesCount, setSalesCount] = useState(0);

  const profile = useMemo<ProfileData>(() => {
    if (userProfile) {
      return {
        fullName: userProfile.fullName,
        preferredName: userProfile.preferredName || undefined,
        cpf: userProfile.cpf,
        email: userEmail || userProfile.email,
        phone: userProfile.phone,
        birthDate: userProfile.birthDate,
        gender: userProfile.gender ?? "prefer-not-to-say",
        address: {
          street: userProfile.address.street,
          number: userProfile.address.number,
          complement: userProfile.address.complement,
          neighborhood: userProfile.address.neighborhood,
          city: userProfile.address.city,
          state: userProfile.address.state,
          cep: userProfile.address.cep,
        },
        profileImage: userProfile.profileImage,
        memberSince: userProfile.memberSince,
        isEmailVerified: userProfile.isEmailVerified,
        isPhoneVerified: userProfile.isPhoneVerified,
        isCpfVerified: userProfile.isCpfVerified,
      };
    }
    const empty = emptyProfileDisplay(userEmail ?? "");
    const name = authUserName?.trim();
    if (name) {
      return { ...empty, fullName: name, email: (userEmail ?? empty.email).trim() || empty.email };
    }
    return empty;
  }, [userProfile, userEmail, authUserName]);

  useEffect(() => {
    if (!photoMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (photoMenuRef.current && !photoMenuRef.current.contains(e.target as Node)) setPhotoMenuOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [photoMenuOpen]);

  useEffect(() => {
    let cancelled = false;
    setAccountStatsLoading(true);
    (async () => {
      let active = 0;
      let sales = 0;
      try {
        const ads = await authClient.getMyAds();
        if (!cancelled) {
          active = ads.filter((a) => (a.status ?? "active") === "active").length;
        }
      } catch {
        /* no token or network */
      }
      try {
        const res = await authClient.getSalesList();
        if (!cancelled) sales = res.sales?.length ?? 0;
      } catch {
        /* no token or network */
      }
      if (!cancelled) {
        setActiveAdsCount(active);
        setSalesCount(sales);
        setAccountStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statsRefreshKey]);

  const handleRemovePhoto = async () => {
    setPhotoMenuOpen(false);
    setPhotoRemoving(true);
    try {
      const updated = await authClient.saveProfile({ profile_image_url: "" });
      onProfileUpdated?.(updated);
    } catch {
      // Optionally show toast; onProfileUpdated not called so UI stays as-is
    } finally {
      setPhotoRemoving(false);
    }
  };

  const displayName = profile.fullName.trim() || profile.email.trim() || "Seu perfil";

  const initials = useMemo(() => {
    const n = profile.fullName.trim();
    if (n) {
      return n
        .split(/\s+/)
        .filter(Boolean)
        .map((name) => name[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    const em = profile.email.trim();
    if (em.length >= 2) return em.slice(0, 2).toUpperCase();
    return "?";
  }, [profile.fullName, profile.email]);

  const hasAddress =
    Boolean(profile.address.street?.trim()) ||
    Boolean(profile.address.cep?.replace(/\D/g, "").length);

  // Email + phone only; identity/CPF is verified via Stripe Connect, not shown here
  const verificationCount = [
    profile.isEmailVerified,
    profile.isPhoneVerified,
  ].filter(Boolean).length;
  const verificationPercentage = Math.round((verificationCount / 2) * 100);

  const genderLabels = {
    male: "Masculino",
    female: "Feminino",
    other: "Outro",
    "prefer-not-to-say": "Prefiro não informar",
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <div>
              <h1 className="text-xl text-white">Meu Perfil</h1>
              <p className="text-sm text-white/80">Gerencie suas informações</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Header Card */}
        <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
          <div className="flex items-start gap-4 mb-6">
            {/* Avatar with Photo Edit / Remove menu */}
            <div className="relative" ref={photoMenuRef}>
              <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-3xl font-medium overflow-hidden">
                {profile.profileImage ? (
                  <img
                    src={profile.profileImage}
                    alt={displayName}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              <button
                type="button"
                disabled={photoRemoving}
                onClick={() => setPhotoMenuOpen((open) => !open)}
                className="absolute bottom-0 right-0 w-8 h-8 bg-primary hover:bg-primary/90 rounded-full flex items-center justify-center shadow-lg transition-colors disabled:opacity-50"
                aria-label="Alterar ou remover foto"
                aria-expanded={photoMenuOpen}
              >
                <Camera className="w-4 h-4 text-primary-foreground" />
              </button>
              {photoMenuOpen && (
                <div className="absolute left-0 top-full mt-2 z-50 min-w-[160px] py-1 bg-card border border-border rounded-lg shadow-lg">
                  <button
                    type="button"
                    onClick={() => { setPhotoMenuOpen(false); onEditProfile(); }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    Alterar foto
                  </button>
                  {profile.profileImage && (
                    <button
                      type="button"
                      disabled={photoRemoving}
                      onClick={handleRemovePhoto}
                      className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      {photoRemoving ? "Removendo…" : "Remover foto"}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <h2 className="text-2xl mb-1">{displayName}</h2>
              <p className="text-sm text-muted-foreground mb-3">
                {profile.memberSince.trim() ? (
                  <>Membro desde {formatDate(profile.memberSince)}</>
                ) : (
                  <span className="text-amber-800 dark:text-amber-200/90">
                    Cadastro incompleto — use &quot;Editar perfil&quot; para adicionar seus dados.
                  </span>
                )}
              </p>

              {/* Verification Status */}
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium">Perfil verificado</span>
                  <span className="text-sm text-muted-foreground">
                    {verificationPercentage}%
                  </span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${verificationPercentage}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {profile.isEmailVerified && (
                  <div className="flex items-center gap-1 text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded-full">
                    <CheckCircle className="w-3 h-3" />
                    <span>E-mail</span>
                  </div>
                )}
                {profile.isPhoneVerified && (
                  <div className="flex items-center gap-1 text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded-full">
                    <CheckCircle className="w-3 h-3" />
                    <span>Telefone</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Edit Button */}
          <Button
            onClick={onEditProfile}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Editar Perfil
          </Button>
        </div>

        {/* Personal Information */}
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-medium flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Informações Pessoais
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Full Name */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Nome Completo
                </label>
                <p className="font-medium">{profile.fullName.trim() || "—"}</p>
              </div>

              {/* Preferred Name */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Nome de Preferência
                </label>
                <p className="font-medium">{profile.preferredName || "—"}</p>
              </div>

              {/* CPF */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  CPF
                </label>
                <p className="font-medium">
                  {profile.cpf.replace(/\D/g, "").length >= 11 ? formatCPF(profile.cpf) : "—"}
                </p>
              </div>

              {/* Birth Date */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Data de Nascimento
                </label>
                <p className="font-medium">
                  {profile.birthDate.trim() ? formatDateOnly(profile.birthDate) : "—"}
                </p>
              </div>

              {/* Gender */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Gênero
                </label>
                <p className="font-medium">{genderLabels[profile.gender]}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-medium flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Informações de Contato
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Email */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <label className="text-sm text-muted-foreground mb-1 block flex items-center gap-2">
                  <Mail className="w-4 h-4 shrink-0" />
                  E-mail
                </label>
                <p className="font-medium break-all" title={profile.email}>
                  {profile.email}
                </p>
              </div>
              {profile.isEmailVerified && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>

            {/* Phone */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label className="text-sm text-muted-foreground mb-1 block flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Telefone/Celular
                </label>
                <p className="font-medium">
                  {profile.phone.replace(/\D/g, "").length >= 8
                    ? formatPhoneNumber(profile.phone)
                    : "—"}
                </p>
              </div>
              {profile.isPhoneVerified && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-medium flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Endereço Principal
            </h3>
          </div>
          <div className="p-6">
            {hasAddress ? (
              <>
                <p className="font-medium mb-1">
                  {profile.address.street}, {profile.address.number}
                  {profile.address.complement && ` - ${profile.address.complement}`}
                </p>
                <p className="text-muted-foreground mb-1">{profile.address.neighborhood}</p>
                <p className="text-muted-foreground mb-1">
                  {profile.address.city} - {profile.address.state}
                </p>
                <p className="text-muted-foreground">
                  CEP:{" "}
                  {profile.address.cep.replace(/\D/g, "").length >= 8
                    ? formatCEP(profile.address.cep)
                    : profile.address.cep || "—"}
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">
                Nenhum endereço cadastrado. Toque em &quot;Editar perfil&quot; para adicionar.
              </p>
            )}
          </div>
        </div>

        {/* Account Statistics */}
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-medium">Estatísticas da Conta</h3>
          </div>
          <div className="grid grid-cols-3 divide-x divide-border">
            <div className="p-4 text-center">
              <p className="text-2xl font-medium text-primary mb-1 min-h-8 flex items-center justify-center">
                {accountStatsLoading ? (
                  <span className="text-muted-foreground text-lg animate-pulse">—</span>
                ) : (
                  activeAdsCount
                )}
              </p>
              <p className="text-xs text-muted-foreground">Anúncios Ativos</p>
            </div>
            <div className="p-4 text-center">
              <p className="text-2xl font-medium text-primary mb-1 min-h-8 flex items-center justify-center">
                {accountStatsLoading ? (
                  <span className="text-muted-foreground text-lg animate-pulse">—</span>
                ) : (
                  salesCount
                )}
              </p>
              <p className="text-xs text-muted-foreground">Vendas Realizadas</p>
            </div>
            <div className="p-4 text-center">
              <p className="text-2xl font-medium text-muted-foreground mb-1">—</p>
              <p className="text-xs text-muted-foreground">Avaliação média</p>
              <p className="text-[10px] text-muted-foreground/80 mt-1">Indisponível até termos avaliações na plataforma</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={(tab) => {
          if (tab === "home" || tab === "favorites") onTabChange(tab);
        }}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
});